import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Image from 'next/image';

const ONLINE_CHECK_INTERVAL = 10000;
const OFFLINE_RETRY_INTERVAL = 1000;
const TIMEOUT = 5000;
const MAX_RETRIES = 3;

const defaultCheckConnection = async (): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);

    const response = await fetch('https://ipv4.icanhazip.com/', {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    return response.ok;
  } catch {
    return false;
  }
};

const NetworkWrapper: React.FC<{
  children: React.ReactNode;
  checkConnectionFn?: () => Promise<boolean>;
}> = ({ children, checkConnectionFn = defaultCheckConnection }) => {
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    let failureCount = 0;
    let timeoutId: NodeJS.Timeout;

    const poll = async () => {
      const isConnected = await checkConnectionFn();

      if (isConnected) {
        failureCount = 0;
        setIsOffline(false);
        timeoutId = setTimeout(poll, ONLINE_CHECK_INTERVAL);
      } else {
        failureCount += 1;
        if (failureCount >= MAX_RETRIES) {
          setIsOffline(true);
        }
        timeoutId = setTimeout(poll, OFFLINE_RETRY_INTERVAL);
      }
    };

    poll();

    return () => clearTimeout(timeoutId);
  }, [checkConnectionFn]);

  return (
    <>
      {children}

      {isOffline && (
        <Modal show backdrop="static" keyboard={false} centered>
          <Modal.Body className="network-body">
            <div className="network-image">
              <Image
                src="/img/Network.svg"
                height={100}
                width={100}
                alt="network"
              />
            </div>
            <div className="network-alert">Oops! We lost connection</div>
            <div className="network-desc">
              We are having trouble connecting to our servers. Please check your
              internet or try again shortly.
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default NetworkWrapper;
